import { useState } from "react";
import { Download, Youtube, ExternalLink, Trash2 } from "lucide-react";
import { videoUrl } from "../lib/api";
import { PublishModal } from "./PublishModal";

export const VideoPreview = ({ project, onPublished }) => {
  const [showPublish, setShowPublish] = useState(false);
  const hasVideo = !!project.video_filename;
  const isPublished = !!project.youtube_video_id;

  return (
    <div className="bg-slate-900/60 border border-slate-800/80 rounded-2xl p-6 backdrop-blur-md">
      {hasVideo ? (
        <div
          className={`mx-auto bg-black rounded-xl overflow-hidden ${
            project.orientation === "shorts" ? "max-w-[340px] aspect-[9/16]" : "aspect-video"
          }`}
        >
          <video
            data-testid="video-preview-player"
            src={videoUrl(project.id)}
            controls
            className="w-full h-full"
          />
        </div>
      ) : (
        <div
          data-testid="media-removed-notice"
          className="mx-auto max-w-md text-center bg-slate-950/60 border border-slate-800 rounded-xl p-8 flex flex-col items-center gap-3"
        >
          <Trash2 className="w-6 h-6 text-slate-600" />
          <p className="text-sm text-slate-400">
            {isPublished
              ? "This video was uploaded to YouTube and removed from server storage to free up space."
              : "Media files for this video were cleared to free up storage space and are no longer available here."}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mt-6 justify-center">
        {project.auto_publish && !isPublished && (
          <span
            data-testid="auto-publish-status-badge"
            className="text-xs font-mono text-amber-400 border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 rounded-full flex items-center gap-1.5"
          >
            <Youtube className="w-3.5 h-3.5" />
            {project.youtube_publish_status === "publishing"
              ? "Auto-publishing to YouTube..."
              : project.youtube_publish_status === "failed"
              ? "Auto-publish failed — publish manually below"
              : project.youtube_publish_status === "skipped_not_connected"
              ? "Auto-publish skipped — YouTube not connected"
              : "Queued to auto-publish to YouTube"}
          </span>
        )}
        {hasVideo && (
          <a
            data-testid="download-video-button"
            href={videoUrl(project.id)}
            download={`${project.topic}.mp4`}
            className="bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700/60 px-4 py-2.5 rounded-xl transition-all flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" /> Download
          </a>
        )}
        {hasVideo && !isPublished && (
          <button
            data-testid="publish-youtube-button"
            onClick={() => setShowPublish(true)}
            className="bg-red-600 hover:bg-red-500 text-white font-medium px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all shadow-md shadow-red-600/30 text-sm"
          >
            <Youtube className="w-4 h-4" /> Publish to YouTube
          </button>
        )}
        {isPublished && (
          <a
            data-testid="watch-on-youtube-link"
            href={`https://youtube.com/watch?v=${project.youtube_video_id}`}
            target="_blank"
            rel="noreferrer"
            className="bg-red-600 hover:bg-red-500 text-white font-medium px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all shadow-md shadow-red-600/30 text-sm"
          >
            <ExternalLink className="w-4 h-4" /> Watch on YouTube
          </a>
        )}
      </div>

      <PublishModal
        project={project}
        open={showPublish}
        onClose={() => setShowPublish(false)}
        onPublished={onPublished}
      />
    </div>
  );
};
